import type {DiffEntry, DiffLine} from '@github-ui/diff-lines'
import type {StateCreator} from 'zustand'
import {loadDiffEntriesForPaths} from '../../page-data/loaders/use-diff-entries'
import {
  diffContextLinesKey,
  fetchInjectedContextLines,
  type ContextLineRange,
} from '../../page-data/loaders/use-context-lines-data'
import {getQueryClient} from '@github-ui/query-client'
import type {ComparisonInfoSlice, ComparisonInfoSliceOpts} from './comparison-info'
import {produce} from 'immer'

export type DiffEntriesSliceOpts = {
  basePath: string
  diffEntries: Record<string, DiffEntry>
  trackDiffEntriesRequests?: boolean
} & Pick<ComparisonInfoSliceOpts, 'baseOid' | 'headOid' | 'selectedCommitRange'>

export type DiffEntriesSlice = {
  diffEntries: Record<string, DiffEntry>
  diffEntriesRequestCount: number

  diffEntriesActions: {
    updateDiffEntries: (diffEntries: DiffEntry[]) => void
    loadDiffEntries: (variables: {
      paths: string[]
      contextRanges?: {[path: string]: Array<[number, number]>}
      signal?: AbortSignal
      isComparisonOutdated?: boolean
    }) => Promise<DiffEntry[]>
    fetchContextLines: (variables: {path: string; contextLineRanges: ContextLineRange[]; w?: string}) => Promise<void>
  }
}

export const createDiffEntriesSlice =
  ({
    basePath,
    diffEntries,
    baseOid,
    headOid,
    selectedCommitRange,
    trackDiffEntriesRequests = false,
  }: DiffEntriesSliceOpts): StateCreator<
    DiffEntriesSlice & ComparisonInfoSlice,
    [['zustand/devtools', never]],
    [],
    DiffEntriesSlice
  > =>
  (set, get) => {
    // we can't call get() here because the comparisonInfo slice may not be initialized yet
    // so we replicate the effectiveRange logic here
    const effectiveRange = selectedCommitRange ?? {baseOid, headOid}

    const queryClient = getQueryClient()
    for (const [_, diffEntry] of Object.entries(diffEntries)) {
      const contextLinesKey = diffContextLinesKey(
        basePath,
        diffEntry.path,
        effectiveRange.baseOid,
        effectiveRange.headOid,
      )

      // Store the diffEntry's diffLines in Tan Stack Queries' context lines cache
      queryClient.setQueryData(contextLinesKey, () => diffEntry.diffLines)
    }

    return {
      diffEntries,
      diffEntriesRequestCount: 0,
      diffEntriesActions: {
        updateDiffEntries: diffEntriesToUpdate => {
          const tempUpdatedDiffEntries: Record<string, DiffEntry> = {}
          for (const diffEntry of diffEntriesToUpdate) {
            // Store difflines in TanStackQuery context lines cache
            const contextLinesKey = diffContextLinesKey(
              basePath,
              diffEntry.path,
              get().comparisonInfo.effectiveRange.baseOid,
              get().comparisonInfo.effectiveRange.headOid,
            )
            queryClient.setQueryData(contextLinesKey, diffEntry.diffLines)
            // Store diffEntry in updatedDiffEntries
            tempUpdatedDiffEntries[diffEntry.path] = diffEntry
          }

          const keys = Object.keys(tempUpdatedDiffEntries)
          set(
            produce(state => {
              for (const diffEntryKey of keys) {
                const diffEntry = tempUpdatedDiffEntries[diffEntryKey]
                if (diffEntry) {
                  state.diffEntries[diffEntryKey] = diffEntry

                  // update progressive-diffs slice here to avoid two zustand updates for perf ❤️
                  const progressiveEntry = state.entriesMap?.[diffEntry.pathDigest]
                  if (progressiveEntry) {
                    progressiveEntry.isLoaded = true
                  }
                }
              }
            }),
            undefined,
            'updateDiffEntriesAndProgressiveDiffs',
          )
        },
        loadDiffEntries: async (variables: {
          paths: string[]
          contextRanges?: {[path: string]: Array<[number, number]>}
          signal?: AbortSignal
          isComparisonOutdated?: boolean
        }) => {
          const loadedEntries = await loadDiffEntriesForPaths({
            basePath,
            ...variables,
            ...get().comparisonInfo,
            isComparisonOutdated: variables.isComparisonOutdated,
            onRequest: trackDiffEntriesRequests
              ? () => {
                  set(
                    state => ({diffEntriesRequestCount: state.diffEntriesRequestCount + 1}),
                    undefined,
                    'recordDiffEntriesRequest',
                  )
                }
              : undefined,
          })
          if (!loadedEntries || loadedEntries.length === 0) {
            return []
          }
          get().diffEntriesActions.updateDiffEntries(loadedEntries)

          return loadedEntries
        },
        fetchContextLines: async ({
          path,
          contextLineRanges,
          w = '0',
        }: {
          path: string
          contextLineRanges: ContextLineRange[]
          w?: string
        }) => {
          const contextLinesKey = diffContextLinesKey(
            basePath,
            path,
            get().comparisonInfo.effectiveRange.baseOid,
            get().comparisonInfo.effectiveRange.headOid,
          )
          const entryToUpdate = get().diffEntries[path]

          if (!entryToUpdate) return

          await queryClient.fetchQuery({
            queryKey: contextLinesKey,
            queryFn: async () => {
              const json = await fetchInjectedContextLines({
                contextLineRanges,
                basePath,
                path,
                w,
                ...get().comparisonInfo,
              })

              let updatedDiffLines = entryToUpdate.diffLines

              const data = queryClient.setQueryData(contextLinesKey, (old: DiffLine[]) => {
                // Fallback to the entries difflines in case Zustand cache has expired
                updatedDiffLines = mergeContextLines(old ?? entryToUpdate.diffLines, json?.diffEntryLines ?? [])
                return updatedDiffLines
              })

              set(
                (prevState: DiffEntriesSlice) => {
                  return {
                    ...prevState,
                    diffEntries: {
                      ...prevState.diffEntries,
                      [path]: {
                        ...entryToUpdate,
                        diffLines: updatedDiffLines,
                      },
                    },
                  }
                },
                undefined,
                'addContextLinesToDiffEntry',
              )

              return data
            },
          })
        },
      },
    }
  }

export function mergeContextLines(initialDiffLines: DiffLine[], expandedDiffLines: DiffLine[]) {
  const lineMap = new Map<string, (typeof initialDiffLines)[0]>()

  for (const line of initialDiffLines) {
    const key = `${line.left}-${line.right}`
    lineMap.set(key, line)
  }

  // using a map for lookups is faster than using a `find` on the array while iterating through the map
  const mergedDiffLines = expandedDiffLines.map(line => {
    const key = `${line.left}-${line.right}`
    const existingLine = lineMap.get(key)

    if (existingLine) {
      return {...line, position: existingLine.position, displayNoNewLineWarning: existingLine.displayNoNewLineWarning}
    } else {
      // diffLines don't actually care about position and we can't comment on context lines anyway
      return {...line, position: null, threadsData: undefined}
    }
  })

  return mergedDiffLines
}
